/* Dustyrosa: stretchy "traveling pill" slider pagination.
   The selected dot becomes a pill that stretches towards the next dot (leading edge first,
   trailing edge catches up) instead of every dot resizing on its own. */
(() => {
  const SELECTOR = '.dr-pagination.dr-slider__dots, .dr-scrollsnap-dots';
  const SKIP = /dr-style-(full|number|dot-outline)/;
  const SELECTED = ['dr-slider__dot--selected', 'dr-scrollsnap-dot__link--active'];
  const PILL = 16;
  const LEAD = 'cubic-bezier(.25, .6, .3, 1)';
  const TRAIL = 'cubic-bezier(.33, .15, .25, 1)';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();

  const dotsOf = (el) => Array.from(el.querySelectorAll(':scope > button'));
  const selectedIndex = (dots) => dots.findIndex((d) => SELECTED.some((c) => d.classList.contains(c)));

  const rest = (el, dot) => {
    /* progress-bar style: the indicator covers the whole segment of the selected dot */
    if (document.documentElement.dataset.drPagination === 'bar') {
      return { left: dot.offsetLeft, right: el.clientWidth - (dot.offsetLeft + dot.offsetWidth) };
    }
    const center = dot.offsetLeft + dot.offsetWidth / 2;
    const left = center - PILL / 2;
    return { left, right: el.clientWidth - (left + PILL) };
  };

  const place = (state, pos) => {
    state.anims.forEach((a) => a.cancel());
    state.anims = [];
    state.ind.style.left = `${pos.left}px`;
    state.ind.style.right = `${pos.right}px`;
  };

  const build = (el) => {
    const dots = dotsOf(el);
    if (dots.length < 2) return null;
    const current = dots[Math.max(0, selectedIndex(dots))];
    let color = getComputedStyle(current).backgroundColor;
    if (!color || color === 'rgba(0, 0, 0, 0)' || color === 'transparent') color = getComputedStyle(el).color;
    el.style.setProperty('--dr-dot-color', color);
    el.style.setProperty('--dr-dot-radius', getComputedStyle(current).borderRadius);
    el.classList.add('dr-dots');

    const ind = document.createElement('span');
    ind.className = 'dr-dots__indicator';
    ind.setAttribute('aria-hidden', 'true');
    el.appendChild(ind);

    const state = { el, ind, count: dots.length, index: -1, anims: [] };
    states.set(el, state);
    return state;
  };

  const move = (state, from, to, steps) => {
    const { ind } = state;
    const cs = getComputedStyle(ind);
    const start = { left: parseFloat(cs.left) || from.left, right: parseFloat(cs.right) || from.right };
    state.anims.forEach((a) => a.cancel());
    state.anims = [];

    ind.style.left = `${to.left}px`;
    ind.style.right = `${to.right}px`;
    if (reduceMotion.matches) return;

    const forward = to.left > start.left;
    const duration = Math.min(620, 440 + steps * 50);
    const lag = duration * 0.04;
    const opts = (easing, delay) => ({ duration, easing, delay, fill: 'backwards' });

    state.anims.push(
      ind.animate([{ left: `${start.left}px` }, { left: `${to.left}px` }], opts(forward ? TRAIL : LEAD, forward ? lag : 0)),
      ind.animate([{ right: `${start.right}px` }, { right: `${to.right}px` }], opts(forward ? LEAD : TRAIL, forward ? 0 : lag))
    );
  };

  const update = (el) => {
    let state = states.get(el);
    const dots = dotsOf(el);
    if (!state || state.count !== dots.length || !el.contains(state.ind)) {
      if (state) state.ind.remove();
      state = build(el);
      if (!state) return;
    }
    const idx = selectedIndex(dots);
    if (idx < 0) return;
    const target = rest(el, dots[idx]);
    if (state.index < 0) {
      place(state, target);
    } else if (idx !== state.index) {
      move(state, rest(el, dots[state.index]), target, Math.abs(idx - state.index));
    }
    state.index = idx;
  };

  const watch = (el) => {
    if (SKIP.test(el.className) || el.dataset.drDots) return;
    el.dataset.drDots = '1';
    update(el);
    new MutationObserver(() => update(el)).observe(el, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    new ResizeObserver(() => {
      const state = states.get(el);
      const dots = dotsOf(el);
      if (!state || state.index < 0 || !dots[state.index]) return;
      place(state, rest(el, dots[state.index]));
    }).observe(el);
  };

  let queued = false;
  const scan = () => {
    queued = false;
    document.querySelectorAll(SELECTOR).forEach(watch);
  };
  const queueScan = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(scan);
  };
  /* only react to DOM changes that actually add a pagination (the theme and apps mutate the page constantly) */
  const hasDots = (n) => n.nodeType === 1 && (n.matches(SELECTOR) || n.querySelector(SELECTOR));

  scan();
  new MutationObserver((muts) => {
    if (queued) return;
    for (const m of muts) {
      for (const n of m.addedNodes) {
        if (hasDots(n)) { queueScan(); return; }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
